/**
 * Normalizacion del texto libre que viaja hacia AWS dentro del payload de un
 * comprobante electronico.
 *
 * El envio del comprobante sale del navegador (`src/atv/eInvoiceApi.ts`) hacia un API
 * Gateway con integracion directa a Step Functions: el body pasa por una plantilla de
 * mapeo VTL antes de convertirse en el `input` de la ejecucion. Esa plantilla interpola
 * el JSON dentro de un literal de JavaScript, asi que hay caracteres que son validos en
 * JSON pero rompen la serializacion aguas abajo (el apostrofo recto se convierte en una
 * secuencia de escape que JSON no admite; U+2028 y U+2029 son ilegales dentro de un
 * literal de JS; un surrogate huerfano no se puede codificar en UTF-8).
 *
 * Vive en `shared/` porque lo usan las dos capas: el frontend lo importa con
 * `@amplifyShared/safeText` y las lambdas con `../shared/safeText`.
 *
 * Tres niveles, de menos a mas invasivo:
 *   1. `stripNonSerializableChars` - solo quita lo que nunca es texto legitimo. Es seguro
 *      incluso sobre JSON serializado, base64 y HTML.
 *   2. `sanitizeTransportText` - nivel 1 mas los reemplazos de caracteres que rompen la
 *      interpolacion. Para texto libre plano.
 *   3. `sanitizeSafeText` - nivel 2 mas colapso de espacios, trim y recorte. Para los
 *      campos que terminan en un nodo del XML de Hacienda.
 */

/**
 * Limite de `<Detalle>` (linea de factura) y de `OtrosCargos/Detalle` en el XSD de
 * Hacienda v4.4.
 */
export const EDOCUMENT_TEXT_MAX_LENGTH = 160;

/**
 * Caracteres de control, excepto tab (U+0009), LF (U+000A) y CR (U+000D): esos tres si
 * aparecen en texto legitimo y el nivel 3 los colapsa a un espacio.
 */
// `no-control-regex` existe para avisar de rangos de control escritos por error; aqui los
// caracteres de control son justamente el objetivo de la expresion.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g;

/**
 * Separadores de linea de Unicode. Son JSON valido, pero ilegales dentro de un literal
 * de JavaScript, que es exactamente lo que produce `$util.escapeJavaScript` en la
 * plantilla de mapeo. Se cambian por un salto de linea normal, no se borran: son
 * separadores de verdad y borrarlos pegaria dos palabras.
 */
const UNICODE_LINE_SEPARATORS = /[\u2028\u2029]/g;

/**
 * Mitades de un par surrogate que quedaron sueltas (tipico de un copiar/pegar que corto
 * un emoji por la mitad, o de un recorte por longitud hecho con `slice`). No se pueden
 * codificar en UTF-8 y son la causa clasica del "unable to marshal / not JSON
 * serializable" de AWS.
 */
const LONE_SURROGATES =
    /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;

/** Apostrofo tipografico: se ve igual que el recto y no se escapa en la plantilla VTL. */
const TYPOGRAPHIC_APOSTROPHE = "\u2019";

const toText = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    return `${value}`;
};

const isPlainObject = (value: object): boolean => {
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
};

/**
 * Nivel 1. Quita unicamente lo que nunca es texto legitimo y no puede atravesar la
 * serializacion. Seguro de aplicar sobre cualquier string, incluidos los que contienen
 * JSON serializado, base64 o HTML.
 */
export const stripNonSerializableChars = (value: unknown): string =>
    toText(value)
        .normalize("NFC")
        .replace(LONE_SURROGATES, "")
        .replace(CONTROL_CHARS, "")
        .replace(UNICODE_LINE_SEPARATORS, "\n");

/**
 * Nivel 2. Nivel 1 mas los reemplazos de los caracteres que rompen la interpolacion del
 * payload en la plantilla de mapeo. No colapsa espacios ni recorta, para no destruir los
 * saltos de linea de un campo multilinea.
 */
export const sanitizeTransportText = (value: unknown): string =>
    stripNonSerializableChars(value)
        // En una descripcion un backtick es casi siempre un acento mal tecleado, y es el
        // caracter que dispara sustitucion de comandos si el texto llega a un shell.
        .replace(/`/g, "")
        .replace(/\\/g, "/")
        .replace(/'/g, TYPOGRAPHIC_APOSTROPHE);

/**
 * Nivel 3. Para los campos que terminan en un nodo del XML de Hacienda: colapsa los
 * espacios repetidos (y los saltos de linea) a uno, hace trim y recorta a `maxLength`.
 *
 * El recorte se hace despues de quitar los surrogates huerfanos, pero puede crear uno
 * nuevo al cortar un emoji por la mitad, asi que se vuelve a limpiar al final.
 */
export const sanitizeSafeText = (value: unknown, maxLength?: number): string => {
    const text = sanitizeTransportText(value).replace(/\s+/g, " ").trim();
    if (maxLength === undefined || text.length <= maxLength) return text;
    return text.slice(0, maxLength).replace(LONE_SURROGATES, "").trimEnd();
};

/** `true` si el texto cambiaria al pasar por `sanitizeTransportText`. */
export const hasUnsafeTextCharacters = (value: unknown): boolean => {
    const text = toText(value);
    return text !== sanitizeTransportText(text);
};

/**
 * Claves cuyo valor NO es texto libre: JSON serializado, base64 o HTML de un editor
 * enriquecido. Reemplazar una barra invertida o un apostrofo ahi corromperia el
 * contenido, asi que reciben solo el nivel 1.
 */
export const RAW_TEXT_KEYS: readonly string[] = [
    "exemption",
    "otros_cargos",
    "customfields",
    "info_references",
    "edocument_status_payload",
    "document_comments",
    "emailBody",
    "awsBody",
    "awsResponse",
];

export type SanitizeDeepOptions = {
    /** Claves que reciben solo el nivel 1. Por defecto `RAW_TEXT_KEYS`. */
    rawTextKeys?: readonly string[];
};

/**
 * Recorre un objeto o array y normaliza todos sus strings antes de serializarlo. Es la
 * ultima linea de defensa del envio: cubre de un golpe las lineas, los datos del cliente,
 * del vendedor y de la suscripcion, no solo la descripcion del producto.
 *
 * No recorta longitudes: el tope del XSD se aplica en el campo concreto que lo necesita.
 * Devuelve estructuras nuevas y no muta la entrada.
 */
export const sanitizeSafeTextDeep = <T>(value: T, options: SanitizeDeepOptions = {}): T => {
    const rawTextKeys = options.rawTextKeys ?? RAW_TEXT_KEYS;

    const walk = (node: unknown, raw: boolean): unknown => {
        if (typeof node === "string") {
            return raw ? stripNonSerializableChars(node) : sanitizeTransportText(node);
        }
        if (Array.isArray(node)) {
            return node.map((item) => walk(item, raw));
        }
        // Solo objetos planos: Date, File y compania se dejan intactos.
        if (node !== null && typeof node === "object" && isPlainObject(node)) {
            const result: Record<string, unknown> = {};
            for (const [key, item] of Object.entries(node)) {
                result[key] = walk(item, raw || rawTextKeys.includes(key));
            }
            return result;
        }
        return node;
    };

    return walk(value, false) as T;
};
